import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Tabs, Tab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { checkLoginIdApi, registerUserApi } from '../../api';
import { PATHS } from '../../routes/paths';
import ConfirmModal from '../../components/common/ConfirmModal';
import Header from '../../components/common/Header';

const RegisterPage = () => {
  const [form, setForm] = useState({
    name: '',
    username: '',
    password: '',
    passwordConfirm: '',
  });

  const [errors, setErrors] = useState({
    name: '',
    username: '',
    password: '',
    passwordConfirm: '',
  });

  const [isUsernameChecked, setIsUsernameChecked] = useState(false);
  const [usernameInfo, setUsernameInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const navigate = useNavigate();

  const handleTabChange = (_event, newValue) => {
    if (newValue === 0) {
      navigate(PATHS.login);
    }
  };

  const handleUsernameCheck = async () => {
    const username = form.username.trim();

    if (!username) {
      setErrors((prev) => ({
        ...prev,
        username: '아이디를 입력해주세요.',
      }));
      setIsUsernameChecked(false);
      setUsernameInfo('');
      return;
    }

    try {
      setCheckingUsername(true);
      const res = await checkLoginIdApi(username);
      if (res?.data?.available === false) {
        setErrors((prev) => ({
          ...prev,
          username: '이미 사용 중인 아이디입니다.',
        }));
        setIsUsernameChecked(false);
        setUsernameInfo('');
      } else {
        setErrors((prev) => ({
          ...prev,
          username: '',
        }));
        setIsUsernameChecked(true);
        setUsernameInfo('사용가능한 아이디입니다.');
      }
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        username: err.message || '아이디 중복확인 중 오류가 발생했습니다.',
      }));
      setIsUsernameChecked(false);
      setUsernameInfo('');
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {
      name: '',
      username: '',
      password: '',
      passwordConfirm: '',
    };

    const trimmedName = form.name.trim();
    if (!trimmedName) {
      newErrors.name = '이름을 입력해주세요.';
    } else if (trimmedName.length > 10) {
      newErrors.name = '이름은 10자 이내로 입력해주세요.';
    }
    if (!form.username.trim()) {
      newErrors.username = '아이디를 입력해주세요.';
    } else if (!isUsernameChecked) {
      newErrors.username = '아이디 중복확인을 해주세요.';
    }
    if (!form.password.trim()) {
      newErrors.password = '비밀번호를 입력해주세요.';
    }
    if (!form.passwordConfirm.trim()) {
      newErrors.passwordConfirm = '비밀번호 확인을 입력해주세요.';
    } else if (form.passwordConfirm !== form.password) {
      newErrors.passwordConfirm = '입력하신 비밀번호와 다릅니다.';
    }

    setErrors(newErrors);
    setUsernameInfo('');
    const hasError = Object.values(newErrors).some((msg) => msg);
    if (hasError) return;

    try {
      setSubmitting(true);
      await registerUserApi({
        name: trimmedName,
        loginId: form.username.trim(),
        password: form.password,
      });
      setSuccessOpen(true);
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        username:
          err.status === 409 && err.message?.includes('아이디')
            ? err.message || '이미 사용 중인 아이디입니다.'
            : prev.username,
        name:
          err.status === 409 && err.message?.includes('이름')
            ? err.message || '이미 사용 중인 이름입니다.'
            : prev.name,
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessConfirm = () => {
    setSuccessOpen(false);
    navigate(PATHS.login);
  };

  return (
    <>
      <Box>
        <Box
          sx={{
            maxWidth: 430,
            mx: 'auto',
            bgcolor: 'white',
            minHeight: '100vh',
            borderRadius: 0,
            boxShadow: '0 0 20px rgba(0,0,0,0.06)',
            px: 2,
            position: 'relative',
          }}
        >
          <Header variant="simple" />

          <Typography
            variant="h6"
            sx={{ fontWeight: 600, textAlign: 'center', mb: 2 }}
          >
            회원가입
          </Typography>

          <Tabs
            value={1}
            onChange={handleTabChange}
            centered
            textColor="primary"
            indicatorColor="primary"
            sx={{
              mb: 3,
              minHeight: 32,
              '& .MuiTab-root': {
                minHeight: 32,
                fontSize: 14,
              },
            }}
          >
            <Tab label="로그인" />
            <Tab label="회원가입" />
          </Tabs>

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{
              mt: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
          <Box sx={{ position: 'relative' }}>
            <TextField
              label="이름"
              fullWidth
              value={form.name}
              onChange={(e) => {
                const value = e.target.value.slice(0, 10);
                setForm((prev) => ({ ...prev, name: value }));
                setErrors((prev) => ({
                  ...prev,
                  name: value
                    ? value.trim().length > 10
                      ? '이름은 10자 이내로 입력해주세요.'
                      : ''
                    : '이름을 입력해주세요.',
                }));
              }}
              error={Boolean(errors.name)}
              helperText={errors.name || ' '}
              inputProps={{ maxLength: 10 }}
              FormHelperTextProps={{
                sx: {
                  mt: 0.5,
                  color: errors.name ? 'error.main' : 'text.secondary',
                },
              }}
            />
          </Box>

          <Box
            sx={{
              position: 'relative',
            }}
          >
            <TextField
              label="아이디"
              fullWidth
              value={form.username}
              onChange={(e) => {
                const value = e.target.value.slice(0, 10);
                setForm((prev) => ({ ...prev, username: value }));
                setIsUsernameChecked(false);
                setErrors((prev) => ({ ...prev, username: value ? '' : prev.username }));
                setUsernameInfo('');
              }}
              error={Boolean(errors.username)}
            helperText={errors.username || usernameInfo || ' '}
              InputProps={{
                sx: {
                  pr: 12,
                },
              }}
              inputProps={{ maxLength: 20 }}
            FormHelperTextProps={{
              sx: {
                mt: 0.5,
                color: errors.username
                  ? 'error.main'
                  : usernameInfo
                  ? 'primary.main'
                  : 'text.secondary',
              },
            }}
            />
            <Button
              variant="outlined"
              sx={{
                position: 'absolute',
                top: '35%',
                right: 16,
                transform: 'translateY(-50%)',
                whiteSpace: 'nowrap',
                px: 2,
                height: 32,
                minWidth: 76,
              }}
              onClick={handleUsernameCheck}
              disabled={checkingUsername}
            >
              중복확인
            </Button>
          </Box>
          <TextField
            label="비밀번호"
            type="password"
            fullWidth
            value={form.password}
            onChange={(e) => {
              const value = e.target.value;
              setForm((prev) => ({ ...prev, password: value }));
              setErrors((prev) => ({ ...prev, password: value ? '' : prev.password }));
            }}
            error={Boolean(errors.password)}
            helperText={errors.password || ' '}
          />
          <TextField
            label="비밀번호 확인"
            type="password"
            fullWidth
            value={form.passwordConfirm}
            onChange={(e) => {
              const value = e.target.value;
              setForm((prev) => ({ ...prev, passwordConfirm: value }));
              setErrors((prev) => ({
                ...prev,
                passwordConfirm:
                  !value
                    ? prev.passwordConfirm
                    : value === form.password
                    ? ''
                    : '입력하신 비밀번호와 다릅니다.',
              }));
            }}
            error={Boolean(errors.passwordConfirm)}
            helperText={errors.passwordConfirm || ' '}
          />

          <Button
            variant="contained"
            type="submit"
            fullWidth
            sx={{
              mt: 1,
              py: 1.2,
              bgcolor: '#0d47a1',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#0b3a81',
              },
            }}
            disabled={submitting}
          >
            회원가입
          </Button>
        </Box>
        </Box>
      </Box>

      <ConfirmModal
        open={successOpen}
        message="회원가입이 완료되었습니다."
        onConfirm={handleSuccessConfirm}
      />
    </>
  );
};

export default RegisterPage;